/* eslint no-return-assign: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from 'react-bootstrap';
import { isEqual, isEmpty, cloneDeep } from 'lodash';
import { Image, Loader } from '@components/common';
import Actions, { thunks } from '@redux/app';
import { updateProgressButton } from '@config/utils';
import { ProgressButton } from '@components';
import { EditImage } from '@components/modals';
import FormField from './formField';

const SETTINGS_TYPE = 'profile';

class ProfileSettings extends Component {
	constructor() {
		super();

		this.state = {
			companyName : '',
		};
	}

	componentDidMount = () => {
		const { Organization } = this.props;
		if (!isEmpty(Organization)) this.setFormState(Organization);
	}

	UNSAFE_componentWillReceiveProps = ({ Organization }) => {
		const lastProps = this.props;
		if (!isEqual(lastProps.Organization, Organization)) this.setFormState(Organization);
	}

	shouldComponentUpdate = ({
		Organization,
		errors,
		buttonState,
		isEdit,
		loading,
	}, { companyName }) => {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(lastProps.Organization.Name, Organization.Name)
			|| !isEqual(lastProps.Organization.OrganizationLogo, Organization.OrganizationLogo)
			|| !isEqual(lastProps.errors, errors)
			|| !isEqual(lastProps.buttonState, buttonState)
			|| !isEqual(lastProps.isEdit, isEdit)
			|| !isEqual(lastProps.loading, loading)
			|| !isEqual(lastState.companyName, companyName)
		);
	};

	componentWillUnmount = () => {
		const { resetForm } = this.props;
		resetForm(SETTINGS_TYPE);
	}

	setFormState = ({ Name : companyName }) => this.setState({ companyName })

	handleOnChange = (property) => (e) => {
		const { errors, setErrors } = this.props;
		const newState = cloneDeep(this.state);

		newState[property] = e.target ? e.target.value : e.value;

		this.setState(newState);

		if (errors[property]) delete errors[property];

		setErrors(SETTINGS_TYPE, errors);
	}

	onCancel = () => {
		const { Organization, resetForm } = this.props;

		resetForm(SETTINGS_TYPE);
		this.setFormState(Organization);
	}

	onSave = (e) => {
		e.preventDefault();
		const { dispatch, setErrors } = this.props;
		const { companyName } = this.state;

		if (!companyName) {
			updateProgressButton({
				dispatch,
				module   : 'app',
				property : `${SETTINGS_TYPE}.buttonState`,
				value    : 'error',
			});
			setErrors(SETTINGS_TYPE, { companyName : true });
			return;
		}

		dispatch(thunks.updateClientDetails({ companyName }));
	}

	uploadImage = (e) => {
		const input = e.target;

		if (!input.files || !input.files[0]) return;

		const reader = new FileReader();
		reader.readAsDataURL(input.files[0]);

		reader.onload = (event) => {
			this._editImage.openModal({
				imageToEdit : event.target.result,
				onSave      : this.saveImage,
				onCancel    : () => this._imagePicker.files = null,
			});
		};
	}

	saveImage = (imageToSend) => {
		const { dispatch } = this.props;

		dispatch(thunks.uploadCompanyImage(imageToSend));
	}

	deleteImage = () => {
		const { dispatch } = this.props;

		dispatch(thunks.deleteCompanyLogo());
	}

	render() {
		const {
			Organization,
			isEdit,
			setEditMode,
			errors,
			buttonState,
			loading,
			setRef,
		} = this.props;
		const { companyName } = this.state;

		return (
			<div
				className="card-section"
				ref={(ref) => setRef(ref)}
			>
				<div className="flex">
					<h5 className="color-dark font-bold margin-bottom-20 flex-1">Profile Settings</h5>
					<If condition={!isEdit}>
						<Button
							bsStyle="link"
							onClick={() => setEditMode(SETTINGS_TYPE, true)}
						>
							<i className="fa fa-pencil margin-right-5" />
							{' '}
							Edit details
						</Button>
					</If>
				</div>
				<FormField
					title="Company Name"
					placeholder="Company Name"
					name="companyName"
					value={isEdit ? companyName : Organization.Name}
					onChange={this.handleOnChange('companyName')}
					isError={errors.companyName}
					isEdit={isEdit}
					hasSeperator
				/>
				<FormField
					title="Company Logo"
					subTitle="JPG max. 500 KB"
					name="companyLogo"
					addOn={(
						<div className="flex-3">
							<Choose>
								<When condition={!Organization.OrganizationLogo}>
									<Button
										bsStyle="link"
										onClick={() => this._imagePicker.click()}
									>
										Add Logo
									</Button>
								</When>
								<Otherwise>
									<Image
										className="height-50 width-50 img-cover border-radius-rounded margin-right-30"
										src={loading ? '' : Organization.OrganizationLogo}
									/>
									<button
										type="button"
										className="btn btn-link text-primary"
										onClick={() => this._imagePicker.click()}
									>
										Update
									</button>
									<button
										type="button"
										className="btn btn-link color-pink-brink margin-left-20"
										onClick={this.deleteImage}
									>
										Delete
									</button>
								</Otherwise>
							</Choose>
							<input
								ref={(ref) => { this._imagePicker = ref; }}
								name="_imagePicker"
								className="hidden"
								type="file"
								onChange={this.uploadImage}
							/>
						</div>
					)}
				/>
				<If condition={isEdit}>
					<div className="width-all text-right margin-top-15">
						<Button
							bsStyle="link"
							className="margin-right-10"
							onClick={this.onCancel}
						>
							Cancel
						</Button>
						<ProgressButton
							type="submit"
							className="btn btn-primary height-42"
							text="Save"
							onClick={this.onSave}
							state={buttonState}
						/>
					</div>
				</If>
				<EditImage ref={(ref) => { this._editImage = ref; }} />
				<Loader active={loading} />
			</div>
		);
	}
}

const mapStateToProps = ({
	app : {
		user    : { Organization = {} },
		[SETTINGS_TYPE] : {
			errors,
			buttonState,
			isEdit,
			loading,
		},
	},
}) => ({
	Organization,
	errors,
	buttonState,
	isEdit,
	loading,
});

const mapDispatchToProps = (dispatch) => ({
	dispatch,
	resetForm   : bindActionCreators(Actions, dispatch).resetForm,
	setErrors   : bindActionCreators(Actions, dispatch).setFormErrors,
	setEditMode : bindActionCreators(Actions, dispatch).setEditMode,
});

ProfileSettings.propTypes = {
	Organization : PropTypes.object,
	buttonState  : PropTypes.string,
	dispatch     : PropTypes.func.isRequired,
	errors       : PropTypes.object.isRequired,
	isEdit       : PropTypes.bool,
	loading      : PropTypes.bool,
	resetForm    : PropTypes.func.isRequired,
	setEditMode  : PropTypes.func.isRequired,
	setErrors    : PropTypes.func.isRequired,
	setRef       : PropTypes.func.isRequired,
};

ProfileSettings.defaultProps = {
	Organization : {},
	buttonState  : '',
	isEdit       : false,
	loading      : false,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileSettings);
